import axios from 'axios';
import { splitEvery } from 'ramda';

const MAX_UPLOAD_REQUEST = 5;

export type FileUpload = {
  file: File;
  uploadUrl: string;
  onUploadFinished?: () => void;
  onUploadFailed?: (error: Error) => void;
};

export type UploadError = {
  error: Error;
  file: File;
};

export const chainFilesUploader = async (uploads: FileUpload[]): Promise<UploadError[]> => {
  if (uploads.length < 1) {
    return [];
  }

  const [{ file, uploadUrl, onUploadFinished, onUploadFailed }] = uploads;
  const uploadErrors: UploadError[] = [];

  try {
    await axios.put(uploadUrl, file, {
      headers: {
        'Content-Type': file.type,
      },
    });
    onUploadFinished?.();
  } catch (error) {
    uploadErrors.push({
      error,
      file,
    });
    onUploadFailed?.(error);
  } finally {
    const remainingUploads = uploads.slice(1);
    const errors = await chainFilesUploader(remainingUploads);
    uploadErrors.push(...errors);
  }

  return uploadErrors;
};

export const getGroupedItems = (array: any[], maxGroupCount: number): any[][] => {
  const groupSize = Math.max(array.length / maxGroupCount, 1);
  return splitEvery(groupSize, array);
};

export const concurrentFilesUploader = async (
  uploads: FileUpload[],
  maxUploadRequest: number = MAX_UPLOAD_REQUEST,
): Promise<UploadError[]> => {
  const groupedUploads = getGroupedItems(uploads, maxUploadRequest);
  const concurrentUploads = groupedUploads.map(chainFilesUploader);
  const allErrors = await Promise.all(concurrentUploads);

  return allErrors.flat();
};
